/**
 * drbooking - Service Analytics Frontend
 * 
 * RGPD COMPLIANT:
 * - Aucune donnée personnelle collectée (pas d'email, nom, téléphone)
 * - Pas d'adresse IP stockée
 * - Session ID anonyme (UUID généré localement, non lié à l'utilisateur)
 * - Respecte le consentement cookies
 * - Données agrégées uniquement côté serveur
 * - Expiration session: fin de navigation
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Génère un ID de session anonyme (non persistant entre sessions)
const generateSessionId = () => {
  // Utilise sessionStorage pour que l'ID expire à la fermeture du navigateur
  let sessionId = sessionStorage.getItem('drbooking_anonymous_session');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('drbooking_anonymous_session', sessionId);
  }
  return sessionId;
};

// Vérifie si l'utilisateur a consenti aux cookies analytics
const hasAnalyticsConsent = () => {
  try {
    const consent = localStorage.getItem('drbooking_cookie_consent');
    if (consent) {
      const parsed = JSON.parse(consent);
      return parsed.analytics === true;
    }
    // Par défaut, pas de tracking sans consentement explicite
    return false;
  } catch {
    return false;
  }
};

// Détecte le pays via l'API (ne stocke pas l'IP)
const detectCountry = async () => {
  try {
    // Utilise une API qui retourne uniquement le pays (pas l'IP)
    const cached = sessionStorage.getItem('drbooking_country');
    if (cached) return cached;
    
    // Fallback: utilise la langue du navigateur pour deviner le pays
    const lang = navigator.language || 'fr-FR';
    const countryMap = {
      'fr': 'FR', 'de': 'DE', 'es': 'ES', 'it': 'IT', 
      'pt': 'PT', 'nl': 'NL', 'ar': 'MA', 'en': 'GB'
    };
    const langCode = lang.split('-')[0].toLowerCase();
    const country = countryMap[langCode] || 'FR';
    
    sessionStorage.setItem('drbooking_country', country);
    return country;
  } catch {
    return 'XX'; // Code inconnu
  }
};

// Catégorise le device (sans stocker le user-agent complet)
const getDeviceCategory = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipod/.test(ua)) return 'mobile';
  if (/tablet|ipad/.test(ua)) return 'tablet';
  return 'desktop';
};

/**
 * Événements trackables (RGPD compliant)
 * Chaque événement contient uniquement des données agrégées/anonymes
 */
const TRACKABLE_EVENTS = {
  // Navigation
  PAGE_VIEW: 'page_view',
  
  // Recherche (sans les termes de recherche pour éviter données sensibles)
  SEARCH_INITIATED: 'search_initiated',
  SEARCH_RESULT_CLICK: 'search_result_click',
  SEARCH_FILTER_USED: 'search_filter_used',
  
  // Rendez-vous (sans identifiants)
  BOOKING_STARTED: 'booking_started',
  BOOKING_SLOT_SELECTED: 'booking_slot_selected',
  BOOKING_COMPLETED: 'booking_completed',
  BOOKING_CANCELLED: 'booking_cancelled',
  BOOKING_RESCHEDULED: 'booking_rescheduled',
  
  // Inscription (type uniquement, pas de données perso)
  REGISTRATION_STARTED: 'registration_started',
  REGISTRATION_COMPLETED: 'registration_completed',
  REGISTRATION_ABANDONED: 'registration_abandoned',
  
  // Login
  LOGIN_STARTED: 'login_started',
  LOGIN_COMPLETED: 'login_completed',
  LOGIN_FAILED: 'login_failed',
  
  // Liste d'attente
  WAITLIST_JOINED: 'waitlist_joined',
  WAITLIST_NOTIFIED: 'waitlist_notified',
  WAITLIST_LEFT: 'waitlist_left',
  
  // Premium
  PREMIUM_PAGE_VIEW: 'premium_page_view',
  PREMIUM_CHECKOUT_STARTED: 'premium_checkout_started',
  PREMIUM_SUBSCRIPTION: 'premium_subscription',
  PREMIUM_TRIAL_STARTED: 'premium_trial_started',
  PREMIUM_CANCELLED: 'premium_cancelled',
  
  // Assistant vocal
  VOICE_ASSISTANT_STARTED: 'voice_assistant_started',
  VOICE_ASSISTANT_COMPLETED: 'voice_assistant_completed',
  VOICE_ASSISTANT_TRANSFERRED: 'voice_assistant_transferred',
  
  // Forum
  FORUM_POST_CREATED: 'forum_post_created',
  FORUM_COMMENT_ADDED: 'forum_comment_added',
  FORUM_POST_LIKED: 'forum_post_liked',
  
  // Favoris
  FAVORITE_ADDED: 'favorite_added',
  FAVORITE_REMOVED: 'favorite_removed',
  FAVORITE_SHARED: 'favorite_shared',
  
  // Documents
  DOCUMENT_UPLOADED: 'document_uploaded',
  DOCUMENT_DOWNLOADED: 'document_downloaded',
  
  // Loyalty
  LOYALTY_POINTS_EARNED: 'loyalty_points_earned',
  LOYALTY_REWARD_REDEEMED: 'loyalty_reward_redeemed',
  
  // Notifications
  NOTIFICATION_SETTINGS_UPDATED: 'notification_settings_updated',
  PUSH_NOTIFICATION_ENABLED: 'push_notification_enabled',
  
  // PWA
  PWA_INSTALLED: 'pwa_installed',
  PWA_OFFLINE_USED: 'pwa_offline_used',
  
  // Erreurs (catégorie uniquement)
  ERROR_OCCURRED: 'error_occurred',
  ERROR_API: 'error_api',
  ERROR_VALIDATION: 'error_validation'
};

/**
 * Envoie un événement au backend de manière anonyme
 * @param {string} eventType - Type d'événement (voir TRACKABLE_EVENTS)
 * @param {object} metadata - Métadonnées anonymes (pas de données perso!)
 */
const trackEvent = async (eventType, metadata = {}) => {
  // Vérifie le consentement RGPD
  if (!hasAnalyticsConsent()) {
    console.debug('[Analytics] Tracking disabled - no consent');
    return;
  }
  
  try {
    const country = await detectCountry();
    const sessionId = generateSessionId();
    
    // Données envoyées (100% anonymes)
    const eventData = {
      event_type: eventType,
      session_id: sessionId,  // ID anonyme, non lié à l'utilisateur
      country_code: country,
      device: getDeviceCategory(),
      referrer: document.referrer ? new URL(document.referrer).hostname : null,
      // Métadonnées anonymes (jamais de données personnelles!)
      metadata: {
        // On filtre les métadonnées pour s'assurer qu'aucune donnée perso ne passe
        page_category: metadata.page_category || null,
        action_type: metadata.action_type || null,
        specialty_type: metadata.specialty_type || null, // "generaliste", pas le nom du médecin
        result_count: metadata.result_count || null,
        success: metadata.success !== undefined ? metadata.success : null
      },
      timestamp: new Date().toISOString()
    };
    
    // Envoi asynchrone (fire and forget)
    fetch(`${API_URL}/api/analytics/track-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData)
    }).catch(() => {
      // Silencieux en cas d'erreur - ne pas bloquer l'UX
    });
    
    console.debug('[Analytics] Event tracked:', eventType);
  } catch (error) {
    // Ne jamais bloquer l'app pour le tracking
    console.debug('[Analytics] Error:', error.message);
  }
};

/**
 * Hook pour tracker automatiquement les pages vues
 */
const trackPageView = (pageName, pageCategory = null) => {
  trackEvent(TRACKABLE_EVENTS.PAGE_VIEW, {
    page_category: pageCategory || pageName
  });
};

/**
 * Logger une visite (appel initial au chargement de l'app)
 */
const logVisit = async () => {
  if (!hasAnalyticsConsent()) return;
  
  try {
    const country = await detectCountry();
    const sessionId = generateSessionId();
    
    await fetch(`${API_URL}/api/analytics/log-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        country_code: country,
        session_id: sessionId,
        referrer: document.referrer || null
      })
    });
    
    console.debug('[Analytics] Visit logged');
  } catch (error) {
    console.debug('[Analytics] Visit log error:', error.message);
  }
};

/**
 * Enregistre le consentement analytics
 * @param {boolean} consent - true si l'utilisateur accepte le tracking
 */
const setAnalyticsConsent = (consent) => {
  try {
    const existing = localStorage.getItem('drbooking_cookie_consent');
    const parsed = existing ? JSON.parse(existing) : {};
    parsed.analytics = consent;
    localStorage.setItem('drbooking_cookie_consent', JSON.stringify(parsed));
    
    // Si consentement donné, log la visite immédiatement
    if (consent) {
      logVisit();
    }
  } catch (error) {
    console.error('[Analytics] Consent save error:', error);
  }
};

// Export des fonctions et constantes
export {
  TRACKABLE_EVENTS,
  trackEvent,
  trackPageView,
  logVisit,
  hasAnalyticsConsent,
  setAnalyticsConsent,
  generateSessionId
};

export default {
  TRACKABLE_EVENTS,
  trackEvent,
  trackPageView,
  logVisit,
  hasAnalyticsConsent,
  setAnalyticsConsent
};
